/**
 * Keeps `<html lang>` equal to the language the page is rendered in (RB-28 item 12).
 *
 * `index.html` ships `lang="en"` and nothing ever moved it, so a Hungarian UI told the
 * browser, a screen reader and the OS spell-checker that it was English: the wrong
 * voice, hyphenation and quote marks, and an offered "translate this page". The
 * attribute carries the LANGUAGE only (`hu`, `de`, …), not the bundle's region, because
 * the region says nothing about the language the text is written in.
 *
 * It listens on i18next rather than at each `changeLanguage` call site, so the
 * Settings dropdown, `useUiLocaleSync`, the invite page and every public page that
 * switches to the client's locale are covered without each remembering to.
 */
import type { i18n as I18nInstance } from 'i18next'

export function htmlLangFor(language: string | undefined): string {
  const primary = language?.split('-')[0]?.toLowerCase()
  return primary || 'en'
}

export function syncHtmlLang(instance: I18nInstance): void {
  const apply = (language?: string) => {
    if (typeof document === 'undefined') return
    document.documentElement.lang = htmlLangFor(language)
  }
  // The detector resolves synchronously, so the language is usually known by the time
  // init() returns; 'initialized' covers the case where it is not.
  apply(instance.language)
  instance.on('initialized', () => apply(instance.language))
  instance.on('languageChanged', apply)
}
